<script>
  import { onMount } from "svelte";
  import {
    Accordion,
    SectionWrapper,
    Tooltip,
    Button,
  } from "paxray-components-svelte";
  import Header from "lib/components/Header.svelte";
  import SideKpi from "lib/components/sideKPI.svelte";
  import ZoomableTreemap from "lib/components/zoomableTreemap/ZoomableTreemap.svelte";
  import AppDrilldownSourceMenu from "../damprojects-frontend/analysis/components/AppDrilldownSourceMenu.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ROUTES } from "@utils/routes.js";
  import { FULLSCREEN_OFF_ICON, FULLSCREEN_ICON, DOWNLOAD_ICON, CATEGORY_ICON } from "@utils/icons.js";

  const rootName = "Process Landscape";
  const hierarchyKeys = ["l1", "l2", "l3", "l4"];
  const minimumShareOptions = [0.5, 1, 2, 5, 10, 20];
  const levelOptions = [
    { text: "L1 Processes", icon: "account_tree" },
    { text: "L2 Subprocesses", icon: "route" },
    { text: "L3 Task Groups", icon: "split" },
    { text: "L4 Tasks", icon: "task" },
  ];
  const metrics = [
    {
      key: "time_pa",
      label: "Annual Time",
      icon: "time_dark",
      formatter: formatDuration,
    },
    {
      key: "frequency_pa",
      label: "Frequency",
      icon: "event_repeat",
      formatter: formatCompact,
    },
    {
      key: "cost_pa",
      label: "Annual Cost",
      icon: "cash",
      formatter: formatCost,
    },
    { key: "users", label: "Users", icon: "user", formatter: formatCompact },
    {
      key: "application_count",
      label: "Applications",
      icon: "apps",
      formatter: formatCompact,
    },
  ];
  const typeColors = {
    management: "#abcfe8",
    core: "#b7e3bd",
    support: "#e5e8a8",
  };

  let processes = [];
  let selectedMetricIndex = 0;
  let selectedLevel = 0;
  let currentPath = [rootName];
  let loading = true;
  let error = "";
  let fullscreen = false;
  let treemap;
  let previousMetricIndex = selectedMetricIndex;
  let minimumShare = 1;

  $: selectedMetric = metrics[selectedMetricIndex];
  $: treeData = buildTree(processes, selectedMetric.key, minimumShare);
  $: nodeColors = Object.fromEntries(
    uniqueL1Processes(processes).map((process) => [
      process.name.toLowerCase(),
      typeColors[process.type] || typeColors.core,
    ]),
  );
  $: selectionRows = getSelectionRows(processes, selectedLevel, currentPath);
  $: selection = summarize(selectionRows);
  $: selectionType = selectedLevel
    ? selectionRows[0]?.type || "core"
    : "portfolio";
  $: levelSelectorOptions = levelOptions.map((option, index) => ({
    ...option,
    disabled: index > selectedLevel || index < selectedLevel - 1,
  }));
  $: portfolioRows = buildPortfolioRows(processes, selectedMetric);
  $: if (selectedMetricIndex !== previousMetricIndex) {
    previousMetricIndex = selectedMetricIndex;
    selectedLevel = 0;
    currentPath = [rootName];
  }

  onMount(async () => {
    await loadProcesses();
  });

  async function loadProcesses() {
    loading = true;
    error = "";
    try {
      const response = await fetchWithSession(
        "/api/monitoring/processes/catalog",
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.message || "Process deep dive could not be loaded.",
        );
      }
      processes = result.processes || [];
      selectedLevel = 0;
      currentPath = [rootName];
    } catch (loadError) {
      error = loadError.message || "Process deep dive could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function buildTree(rows, metricKey, minimumShareValue) {
    function buildLevel(levelRows, depth) {
      const grouped = new Map();
      levelRows.forEach((row) => {
        const name =
          row[hierarchyKeys[depth]] ||
          `Unassigned ${hierarchyKeys[depth].toUpperCase()}`;
        if (!grouped.has(name)) grouped.set(name, []);
        grouped.get(name).push(row);
      });
      return [...grouped.entries()].map(([name, children]) =>
        depth === hierarchyKeys.length - 1
          ? {
              name,
              value: Math.max(
                children.reduce(
                  (total, row) => total + Number(row[metricKey] || 0),
                  0,
                ),
                0.0001,
              ),
            }
          : { name, children: buildLevel(children, depth + 1) },
      );
    }

    const l1Groups = new Map();
    rows.forEach((row) => {
      if (!l1Groups.has(row.l1)) l1Groups.set(row.l1, []);
      l1Groups.get(row.l1).push(row);
    });
    const total = rows.reduce(
      (sum, row) => sum + Number(row[metricKey] || 0),
      0,
    );
    const minimumValue = total * (Number(minimumShareValue) / 100);
    const visibleRows = [];
    let othersValue = 0;
    l1Groups.forEach((groupRows) => {
      const value = groupRows.reduce(
        (sum, row) => sum + Number(row[metricKey] || 0),
        0,
      );
      if (!total || value >= minimumValue) visibleRows.push(...groupRows);
      else othersValue += value;
    });

    const children = visibleRows.length ? buildLevel(visibleRows, 0) : [];
    if (othersValue > 0) children.push({ name: "Others", value: othersValue });

    return {
      name: rootName,
      children,
    };
  }

  function getSelectionRows(rows, level, path) {
    if (!level) return rows;
    return rows.filter((row) => {
      for (let pathIndex = 0; pathIndex < level; pathIndex += 1) {
        const hierarchyIndex = level - pathIndex - 1;
        if (row[hierarchyKeys[hierarchyIndex]] !== path[pathIndex])
          return false;
      }
      return true;
    });
  }

  function summarize(rows) {
    const frequency = rows.reduce(
      (total, row) => total + Number(row.frequency_pa || 0),
      0,
    );
    const durationWeight = rows.reduce(
      (total, row) => total + Math.max(Number(row.frequency_pa || 0), 1),
      0,
    );
    return {
      time_pa: rows.reduce((total, row) => total + Number(row.time_pa || 0), 0),
      cost_pa: rows.reduce((total, row) => total + Number(row.cost_pa || 0), 0),
      frequency_pa: frequency,
      avg_time: durationWeight
        ? rows.reduce(
            (total, row) =>
              total +
              Number(row.avg_time || 0) *
                Math.max(Number(row.frequency_pa || 0), 1),
            0,
          ) / durationWeight
        : 0,
      applications: new Set(rows.flatMap((row) => row.applications || [])).size,
      teams: new Set(rows.flatMap((row) => row.teams || [])).size,
      tasks: rows.length,
    };
  }

  function uniqueL1Processes(rows) {
    const values = new Map();
    rows.forEach((row) => {
      if (!values.has(row.l1))
        values.set(row.l1, { name: row.l1, type: row.type });
    });
    return [...values.values()];
  }

  function buildPortfolioRows(rows, metric) {
    const metricValue = (rows) => {
      if (metric.key === "users") {
        return Math.max(0, ...rows.map((row) => Number(row.users || 0)));
      }
      if (metric.key === "application_count") {
        return new Set(rows.flatMap((row) => row.applications || [])).size;
      }
      return rows.reduce((sum, row) => sum + Number(row[metric.key] || 0), 0);
    };
    const total = metricValue(rows);
    return ["management", "core", "support"].map((type) => {
      const typeRows = rows.filter((row) => row.type === type);
      const value = metricValue(typeRows);
      return {
        type,
        value,
        share: total ? (value / total) * 100 : 0,
        processes: new Set(typeRows.map((row) => row.l1)).size,
      };
    });
  }

  function formatDuration(seconds) {
    const hours = Number(seconds || 0) / 3600;
    if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k h`;
    if (hours >= 1) return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
    return `${Math.round(Number(seconds || 0) / 60)} min`;
  }

  function formatCompact(value) {
    return new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(Number(value || 0));
  }

  function formatCost(value) {
    return `${formatCompact(value)} €`;
  }

  function typeLabel(value) {
    return String(value || "portfolio").replace(/^./, (letter) =>
      letter.toUpperCase(),
    );
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Process Intelligence"
  areaClickable={false}
  breadcrumb="Process Deep Dive"
  tooltipText="Hierarchical process treemap from L1 to L4. Select an L1 area and drill through its L2 subprocesses and L3 task groups to the contained L4 tasks."
/>

<SectionWrapper
  {fullscreen}
  fitScreen
  wrapperClass="process-deep-dive-wrapper"
  canvasClass="process-deep-dive-canvas"
>
  <div class="deep-dive-main" slot="main">
    <div class="treemap-controls">
      <div class="selectors">
        <AppDrilldownSourceMenu
          bind:selected={selectedLevel}
          featureControlled={false}
          options={levelSelectorOptions}
        />
        <AppDrilldownSourceMenu
          bind:selected={selectedMetricIndex}
          featureControlled={false}
          options={metrics.map((metric) => ({
            text: metric.label,
            icon: metric.icon,
          }))}
        />
      </div>
      <div class="actions">
        <Button
          kind="secondary"
          size="field"
          icon={DOWNLOAD_ICON}
          iconDescription="Download treemap"
          on:click={() => treemap?.downloadSVG()}
        />
        <Button
          kind="secondary"
          size="field"
          icon={fullscreen ? FULLSCREEN_OFF_ICON : FULLSCREEN_ICON}
          on:click={() => {
            fullscreen = !fullscreen;
          }}
        />
      </div>
    </div>

    <div class="treemap-surface">
      {#if error}
        <div class="empty-state error">{error}</div>
      {:else if !loading && !processes.length}
        <div class="empty-state">
          Transfer calculated process results to start the process deep dive.
        </div>
      {:else}
        {#key treeData}
          <ZoomableTreemap
            bind:this={treemap}
            bind:currentApp={currentPath}
            bind:level={selectedLevel}
            {treeData}
            {loading}
            formatfunction={selectedMetric.formatter}
            responsiveLabels
            {nodeColors}
          />
        {/key}
      {/if}
    </div>
  </div>

  <div class="deep-dive-menu" slot="menu">
    <Accordion heading="Current Selection" open>
      <div class="kpi-list">
        {#if selectedLevel}
          <SideKpi name="Process Type" value={typeLabel(selectionType)} />
        {/if}
        <SideKpi name="L4 Tasks" value={selection.tasks} />
        <SideKpi name="Teams" value={selection.teams} />
        <SideKpi name="Apps" value={selection.applications} />
        <SideKpi name="Annual Time" value={formatDuration(selection.time_pa)} />
        <SideKpi
          name="Frequency per Year"
          value={formatCompact(selection.frequency_pa)}
        />
        <SideKpi
          name="Avg. Duration"
          value={formatDuration(selection.avg_time)}
        />
        <SideKpi name="Annual Cost" value={formatCost(selection.cost_pa)} />
      </div>
    </Accordion>

    <Accordion heading="Process Portfolio" open>
      <div class="portfolio-settings">
        <label for="process-minimum-share">Minimum share to show isolated</label
        >
        <select
          id="process-minimum-share"
          value={minimumShare}
          on:change={(event) => {
            minimumShare = Number(event.target.value);
            selectedLevel = 0;
            currentPath = [rootName];
          }}
        >
          {#each minimumShareOptions as option}
            <option value={option}>{option}%</option>
          {/each}
        </select>
      </div>
      <div class="portfolio-list">
        {#each portfolioRows as row}
          <div class="portfolio-row">
            <span class={`type-swatch ${row.type}`}></span>
            <div><strong>{typeLabel(row.type)}</strong></div>
            <Tooltip
              text={`${row.processes} L1 processes in this process group.`}
              showIcon={false}
            >
              <span slot="trigger">{row.share.toFixed(0)}%</span>
            </Tooltip>
          </div>
        {/each}
      </div>
      <div class="assignment-placeholder">
        <div class="assignment-label">
          <strong>Processes without Type</strong>
          <strong>0%</strong>
        </div>
        <div class="assignment-actions">
          <Button
            kind="secondary"
            size="field"
            icon={CATEGORY_ICON}
            disabled
            iconDescription="Process type assignment is not available yet"
          />
        </div>
      </div>
    </Accordion>
  </div>
</SectionWrapper>

<style>
  :global(.process-deep-dive-wrapper .view_wrapper) {
    background: var(--pax-smoke);
  }

  .deep-dive-main {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 100%;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }

  .treemap-controls {
    display: flex;
    width: 100%;
    min-height: 2.5rem;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem 0.5rem;
    box-sizing: border-box;
  }

  .selectors,
  .actions {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.45rem;
  }

  .treemap-surface {
    width: 100%;
    height: auto;
    min-height: 0;
    flex: 1 1 auto;
    box-sizing: border-box;
    padding-top: 0.15rem;
    padding-bottom: 0.75rem;
    overflow: hidden;
  }

  .treemap-surface :global(.treemap_wrapper) {
    min-height: 0;
    padding-bottom: 0;
  }
  .treemap-surface :global(.chart) {
    height: auto;
    min-height: 0;
    flex: 1 1 auto;
  }

  .deep-dive-menu {
    display: flex;
    height: calc(100vh - 6rem);
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    scrollbar-width: none;
    font-size: inherit;
  }
  .deep-dive-menu::-webkit-scrollbar {
    display: none;
  }
  .deep-dive-menu :global(.accordion_heading) {
    font-size: inherit;
  }
  .type-swatch.management {
    background: #abcfe8;
  }
  .type-swatch.core {
    background: #b7e3bd;
  }
  .type-swatch.support {
    background: #e5e8a8;
  }
  .kpi-list {
    display: grid;
    gap: 0.5rem;
  }
  .deep-dive-menu :global(.kpi_wrapper) {
    height: 2.25rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
  }
  .deep-dive-menu :global(.kpi_wrapper .type),
  .deep-dive-menu :global(.kpi_wrapper .value) {
    font-size: inherit;
  }

  .portfolio-settings {
    display: flex;
    min-height: 2.25rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
    background: var(--pax-smoke);
  }

  .portfolio-settings label {
    min-width: 0;
    font: inherit;
  }
  .portfolio-settings select {
    height: 2rem;
    min-width: 4.25rem;
    flex: 0 0 auto;
    padding: 0 0.5rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font: inherit;
  }

  .portfolio-list {
    display: grid;
    gap: 0.5rem;
  }
  .portfolio-row {
    display: grid;
    min-height: 2.25rem;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
    background: var(--pax-smoke);
  }
  .type-swatch {
    width: 0.85rem;
    height: 0.85rem;
    border: 1px solid rgb(28 28 32 / 25%);
    border-radius: 0.15rem;
  }
  .portfolio-row div {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }
  .portfolio-row strong {
    color: var(--pax-black);
    font-size: inherit;
  }
  .portfolio-row :global(.tooltip) {
    color: var(--pax-black);
    font-size: inherit;
    font-weight: 700;
  }

  .assignment-placeholder {
    display: flex;
    min-height: 3.5rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.65rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #d9d9d9;
  }
  .assignment-label {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 0.35rem;
    color: var(--pax-black);
    font-size: 1rem;
  }
  .assignment-label strong {
    font-weight: 500;
  }
  .assignment-label strong:last-child {
    font-weight: 700;
  }
  .assignment-actions {
    display: flex;
    align-items: center;
    color: var(--pax-black);
  }
  .assignment-actions :global(.button_wrapper) {
    cursor: not-allowed;
  }

  .empty-state {
    display: grid;
    height: 100%;
    min-height: 20rem;
    place-items: center;
    color: #6c7580;
    font-size: 0.75rem;
    text-align: center;
  }
  .empty-state.error {
    color: #da1e28;
  }

  @media (max-width: 70rem) {
    .treemap-controls {
      align-items: flex-start;
    }
    .selectors {
      flex-wrap: wrap;
    }
  }
</style>
